import * as THREE from 'three'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'

import BaseObject from './baseObject'
import LabelObject from '@glass-designer/objects/labelObject'

import GlassDesignerTypes from '../types'


// Linear Ruler Object
// Displays a ruler display object that shows the start/end point & distance
// Used to display changes when editing dimensions / shape / etc
export default class LinearRulerObject extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)

    this.persistable = false
    this.startPosition = new THREE.Vector2(0, 0)
    this.endPosition = new THREE.Vector2(0, 0)
    this.outDirection = new THREE.Vector2(0, -1)
    this.colour = options.colour || '#000000'
    this.opacity = parseFloat(options.opacity || 1.0)
    this.displayAsChange = !!options.displayAsChange
    this.measurementPositive = true

    this.lineObj = null
    this.objectGeometry = null

    this.sceneObject = new THREE.Object3D()

    this.measureLabelObj = new LabelObject(editor, {
      colour: this.colour,
      opacity: this.opacity,
      fontSize: 22,
      anchorX: 'center',
      anchorY: 'top'
    })
    this.addChildObject(this.measureLabelObj)

    this.updateSceneObject()
  }

  /**
   * Dispose the object
   */
  dispose() {
    super.dispose()

    if(this.objectGeometry) {
      this.objectGeometry.dispose()
      this.objectGeometry = null
    }
    this.lineObj = null
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()
  }


  // Update the start position of the ruler
  setStartPosition(pos) {
    this.startPosition.set(pos.x, pos.y)
  }

  // Update the end position of the ruler
  setEndPosition(pos) {
    this.endPosition.set(pos.x, pos.y)
  }

  // Update the out direction of the ruler
  setOutDirection(dir) {
    this.outDirection.set(dir.x, dir.y)
  }

  // Set the measurement change as positive/negative
  setMeasurementPositive(measurement) {
    this.measurementPositive = measurement
  }


  /**
   * Build the scene object
   */
  updateSceneObject() {
    let outDistance = 150
    let edgeLength = 30
    let arrowWidth = 7
    let arrowLength = 12

    if(this.objectGeometry) {
      this.objectGeometry.dispose()
      this.objectGeometry = null
    }
    this.lineObj = null

    this.clearSceneObject()

    var startPos = this.startPosition.clone().addScaledVector(this.outDirection, outDistance)
    var endPos = this.endPosition.clone().addScaledVector(this.outDirection, outDistance)
    var dir = endPos.clone().sub(startPos)
    dir.normalize()

    let startEdgePosA = this.startPosition.clone()
    let startEdgePosB = startEdgePosA.clone().addScaledVector(this.outDirection, outDistance + edgeLength)
    let endEdgePosA = this.endPosition.clone()
    let endEdgePosB = endEdgePosA.clone().addScaledVector(this.outDirection, outDistance + edgeLength)

    let distance = Math.round(this.startPosition.distanceTo(this.endPosition))

    // Build line
    var vertices = []
    vertices.push(startEdgePosA.x, startEdgePosA.y, 0)
    vertices.push(startEdgePosB.x, startEdgePosB.y, 0)
    vertices.push(startPos.x, startPos.y, 0)
    vertices.push(endPos.x, endPos.y, 0)
    
    // Arrow
    vertices.push(endPos.x, endPos.y, 0, endPos.x - dir.x * arrowLength - this.outDirection.x * arrowWidth, endPos.y - dir.y * arrowLength - this.outDirection.y * arrowWidth, 0)
    vertices.push(endPos.x, endPos.y, 0, endPos.x - dir.x * arrowLength + this.outDirection.x * arrowWidth, endPos.y - dir.y * arrowLength + this.outDirection.y * arrowWidth, 0)
    
    vertices.push(endEdgePosA.x, endEdgePosA.y, 0)
    vertices.push(endEdgePosB.x, endEdgePosB.y, 0)

    // Distance label
    let midpoint = new THREE.Vector2((endPos.x + startPos.x) / 2, (endPos.y + startPos.y) / 2)
    var textOutOffset = 0
    if(distance < 20)
      textOutOffset += edgeLength + 5

    this.measureLabelObj.setAnchorX(dir.x < 0 ? 'right' : 'left')
    this.measureLabelObj.setAnchorY('top')
    this.measureLabelObj.setPosition(endPos)

    // If line is vertical, show label at the origin line
    if(Math.abs(this.outDirection.x) > 0.5) {
      this.measureLabelObj.setAnchorX(this.outDirection.x > 0 ? 'left' : 'right')
      this.measureLabelObj.setAnchorY(dir.y > 0 ? 'top' : 'bottom')
      this.measureLabelObj.setPosition(endPos)
    }

    var prefix = ''
    if(this.displayAsChange && distance > 0)
      prefix = this.measurementPositive ? '+' : '-'

    this.measureLabelObj.setText(`${prefix}${distance}mm`)

    this.objectGeometry = new THREE.BufferGeometry()
    this.objectGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))

    var lineMaterial = new THREE.LineBasicMaterial({
      color: this.colour,
      transparent: true,
      opacity: this.opacity,
    })
    this.lineObj = new THREE.LineSegments(this.objectGeometry, lineMaterial)
    this.lineObj.computeLineDistances()
    this.sceneObject.add(this.lineObj)
  }
}
